import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/chadcn/ContextMenu"
import {
  EmrNotificationApiDto,
  NotificationLogEntryStatus,
} from "@/model/Patient"
import { isToday } from "@/utils/calendar-utils"
import cn from "@/utils/cn"
import { notificationTypeBgColor } from "@/utils/notification-utils"
import { DateTimeFormatter, Instant, LocalDate, ZoneId } from "@js-joda/core"
import { Locale } from "@js-joda/locale"

const dateFormatter = DateTimeFormatter.ofPattern("MMM d").withLocale(
  Locale.US
)
const timeFormatter = DateTimeFormatter.ofPattern("h:mm a").withLocale(
  Locale.US
)

type EMRNotificationCellProps = {
  date: LocalDate
  notifications: EmrNotificationApiDto[]
  clinicTimeZone: string
  isHighlighted: boolean
  isOutsideMonth: boolean
  onNavigateToRow: (notificationId: string) => void
}

const getStatusBadgeClass = (status: NotificationLogEntryStatus) => {
  switch (status) {
    case "SENT":
      return "bg-green-100 text-green-700"
    case "PENDING":
    case "SENDING":
      return "bg-yellow-100 text-yellow-700"
    case "SEND_FAILED":
      return "bg-red-100 text-red-700"
    case "SKIPPED":
    case "DISABLED":
    case "INACTIVE_PRESCRIPTION":
      return "bg-gray-100 text-gray-700"
  }
}

const EMRNotificationItem = ({
  notification,
  clinicTimeZone,
  onNavigateToRow,
  isSent,
}: {
  notification: EmrNotificationApiDto
  clinicTimeZone: string
  onNavigateToRow: (notificationId: string) => void
  isSent: boolean
}) => {
  const timestamp = notification.sentAt
    ? Instant.parse(notification.sentAt)
    : Instant.parse(notification.enqueuedAt)

  return (
    <ContextMenu key={notification.id}>
      <ContextMenuTrigger asChild>
        <div
          className={cn(
            "flex cursor-context-menu flex-col gap-1 rounded-lg p-2",
            notificationTypeBgColor(notification.type),
            !isSent && "opacity-50"
          )}
        >
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs text-primary/70">
              {timeFormatter.format(
                timestamp.atZone(ZoneId.of(clinicTimeZone))
              )}
            </span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-medium",
                getStatusBadgeClass(notification.status)
              )}
            >
              {notification.status}
            </span>
          </div>
          <span className="break-all text-xs text-primary/90">
            {notification.type}
          </span>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => onNavigateToRow(notification.id)}>
          Jump to table row
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}

const EMRNotificationCell = ({
  date,
  notifications,
  clinicTimeZone,
  isHighlighted,
  isOutsideMonth,
  onNavigateToRow,
}: EMRNotificationCellProps) => {
  const hasData = notifications.length > 0
  const isTodayDate = isToday(date)

  const sentNotifications = notifications.filter(
    (n) => n.sentAt && n.status === "SENT"
  )
  const enqueuedNotifications = notifications.filter(
    (n) => !n.sentAt || n.status !== "SENT"
  )

  return (
    <div
      id={`calendar-cell-${date.toString()}`}
      className={cn(
        "flex min-h-[100px] flex-col rounded-lg border border-gray-300 p-2",
        isTodayDate && "border-2 border-blue-500",
        !isHighlighted ? "bg-white" : "animate-highlightPulse"
      )}
    >
      <div className="flex items-center justify-end">
        <p
          className={cn(
            "text-nowrap text-sm",
            isOutsideMonth ? "text-primary/40" : "text-primary/90",
            isTodayDate && "font-bold text-blue-500"
          )}
        >
          {dateFormatter.format(date)}
        </p>
      </div>
      {!hasData ? (
        <div className="flex h-full items-center justify-center text-primary/40">
          -
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-1.5 overflow-y-auto">
          {sentNotifications.map((notification) => (
            <EMRNotificationItem
              key={notification.id}
              notification={notification}
              clinicTimeZone={clinicTimeZone}
              onNavigateToRow={onNavigateToRow}
              isSent={true}
            />
          ))}

          {sentNotifications.length > 0 && enqueuedNotifications.length > 0 && (
            <div className="my-1 border-t border-gray-300" />
          )}

          {enqueuedNotifications.map((notification) => (
            <EMRNotificationItem
              key={notification.id}
              notification={notification}
              clinicTimeZone={clinicTimeZone}
              onNavigateToRow={onNavigateToRow}
              isSent={false}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default EMRNotificationCell
